import {
  bareDomainOf,
  companyIdentifierChecks,
  websiteDomainOf,
} from "@/lib/company-mention-reliability";
import { withoutBrief } from "@/lib/company-tag";
import type {
  CompaniesFromTagsDTO,
  CompaniesFromTagsRequestDTO,
  CompanyDTO,
  CompanyFromTagDTO,
  CompanyFromTagRequestDTO,
  CompanyFromTagSkipReason,
  CompanyTag,
  CompanyWebKeyData,
} from "@shaarp/common";
import type { AxiosInstance } from "axios";

/**
 * The website the people of a tag not in base are searched by, bare: the
 * tag's, then the web profile's — the contact search reads a domain, so a
 * SIRET or SIREN alone suggests nobody. A value counts only when it is no
 * directory's or social network's domain — one would list its own staff —
 * since the tag and the profile are model output. `null` when neither
 * qualifies.
 */
export function companyTagContactDomain({
  tag,
  keyData,
}: {
  tag: Pick<CompanyTag, "domain">;
  keyData?: Pick<CompanyWebKeyData, "website"> | null;
}): string | null {
  for (const value of [tag.domain, websiteDomainOf(keyData?.website)]) {
    if (value && companyIdentifierChecks({ domain: value }).domain === "valid") {
      return bareDomainOf(value)?.toLowerCase() ?? null;
    }
  }
  return null;
}

/**
 * `POST /company/from-tag`: saves the company a tag designates — found in
 * Shaarp or created, from its stored web profile (`docs/decisions/0016`) — or
 * says why none was. The tag's `brief` stays in the browser.
 */
export async function postCompanyFromTag(
  api: AxiosInstance,
  tag: CompanyTag,
): Promise<CompanyFromTagDTO> {
  const body: CompanyFromTagRequestDTO = { tag: withoutBrief(tag) };
  const { data } = await api.post<CompanyFromTagDTO>("/company/from-tag", body);
  return data;
}

/**
 * `POST /companies/from-tags`: saves the companies a batch of tags designates
 * — at most `COMPANY_FROM_TAGS_BATCH_MAX` — each as `postCompanyFromTag` saves
 * one, and answers what each was saved as, in order. The tags' `brief` stays
 * in the browser.
 */
export async function postCompaniesFromTags(
  api: AxiosInstance,
  tags: readonly CompanyTag[],
): Promise<CompanyFromTagDTO[]> {
  const body: CompaniesFromTagsRequestDTO = { tags: tags.map(withoutBrief) };
  const { data } = await api.post<CompaniesFromTagsDTO>(
    "/companies/from-tags",
    body,
  );
  return data.results;
}

/** The company a tag was saved as, or why none was. */
export type CompanyTagSave =
  | { status: "company"; company: CompanyDTO }
  | { status: "skipped"; reason: CompanyFromTagSkipReason };

/**
 * Saves the company a tag not in base designates, on a click on one of its
 * panel's actions, through `POST /company/from-tag` — which searches it by its
 * identifiers before creating it, and reuses its stored web profile
 * (`docs/decisions/0016`). Answers the team's view of the company, read once
 * the route has saved it.
 */
export async function saveCompanyTag(
  api: AxiosInstance,
  tag: CompanyTag,
): Promise<CompanyTagSave> {
  const saved = await postCompanyFromTag(api, tag);
  if (saved.status === "skipped") return saved;
  const { data: company } = await api.get<CompanyDTO>(
    `/company/available/${saved.companyId}`,
  );
  return { status: "company", company };
}
